import { useCallback, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import styles from './Modal.module.css';

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly wide?: boolean;
  /** Etichetta del bottone di chiusura. Tradotta dal chiamante. */
  readonly closeLabel?: string;
  /**
   * `false` impedisce la chiusura con ESC e col clic sul backdrop.
   * Da usare SOLO per conferme distruttive: bloccare ESC su un dialogo
   * informativo è una trappola per chi naviga da tastiera.
   */
  readonly dismissible?: boolean;
}

/**
 * Modale costruita su `<dialog showModal()>`.
 *
 * Accessibilità delegata al browser (e quindi corretta per costruzione):
 *  · focus trap dentro il dialogo
 *  · `inert` implicito sul resto del documento (nessun `aria-hidden` a mano)
 *  · ESC chiude (evento `cancel`)
 *  · ruolo `dialog` e `aria-modal` impliciti
 *
 * Accessibilità aggiunta qui:
 *  · `aria-labelledby` sul titolo e `aria-describedby` sulla descrizione
 *  · il focus va al primo elemento interattivo all'apertura, e TORNA
 *    all'elemento che ha aperto il dialogo alla chiusura (WCAG 2.4.3)
 *  · clic sul backdrop = chiusura, ma solo se `dismissible`
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide = false,
  closeLabel = 'Chiudi',
  dismissible = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      // jsdom non implementa showModal in tutte le versioni: la guardia
      // evita che il test si rompa su un dettaglio d'ambiente.
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      // WCAG 2.4.3: il focus torna da dove è partito.
      previouslyFocused.current?.focus();
    }
  }, [open]);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // `cancel` è l'evento di ESC. Sempre prevenuto: la chiusura passa da
      // onClose, così lo stato React resta la fonte di verità.
      event.preventDefault();
      if (dismissible) onClose();
    },
    [dismissible, onClose],
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (!dismissible) return;
      // Il click sul backdrop arriva con target === il dialog stesso.
      if (event.target === dialogRef.current) onClose();
    },
    [dismissible, onClose],
  );

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, wide ? styles.wide : null].filter(Boolean).join(' ')}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
    >
      {/* Il div interno assorbe i click, così solo quelli sul backdrop chiudono. */}
      <div onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className={styles.description}>
                {description}
              </p>
            ) : null}
          </div>
          {dismissible ? (
            <Button variant="ghost" iconOnly aria-label={closeLabel} onClick={onClose}>
              <span aria-hidden="true">✕</span>
            </Button>
          ) : null}
        </div>

        <div className={styles.body}>{children}</div>

        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </dialog>
  );
}
